import { type ReactNode } from 'react';
import { type ComplianceItem, type FooterItemType, type FooterLevelTwoDisclaimer } from '@cx-web/domain-types';

import { type SocialLink } from '../../types';

import styles from './FooterSocialsAndDisclaimer.module.css';

const SocialLinkAnchor = ({ SvgIcon, href, name }: { SvgIcon: SocialLink['SvgIcon']; href: string; name: string }) => {
  return (
    <a
      className={styles.footerSocialsLink}
      data-testid="social-link"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
      title={name}
    >
      <SvgIcon aria-label={`${name} icon`} className={styles.footerSocialsImage} />
    </a>
  );
};

const ComplianceLogo = ({ ctaUri, imageUri }: { ctaUri: string; imageUri: string }) => {
  return (
    <a
      className={styles.complianceLogoLink}
      data-testid="compliance-logo-link"
      href={ctaUri}
      rel="noopener noreferrer"
      target="_blank"
      title="Compliance Logo"
    >
      {/* NGF-6364: lazy-load this below-fold footer image so it doesn't compete with the LCP fetch.
          width/height are intentionally omitted — imageUri is a CMS-driven URL with no known
          dimensions ("where controllable"). Shared component, so RR7 atlas benefits too. */}
      <img alt="Compliance Logo" loading="lazy" src={imageUri} />
    </a>
  );
};

type FooterSocialsAndDisclaimerProps = {
  legalAge: string;
  legalText: string;
  levelTwo: FooterLevelTwoDisclaimer[];
  logo: ReactNode;
  logoType: FooterItemType;
  socialLinks: SocialLink[];
};

export const FooterSocialsAndDisclaimer = ({
  legalAge,
  legalText,
  levelTwo,
  logo,
  logoType,
  socialLinks,
}: FooterSocialsAndDisclaimerProps) => {
  const socialsAndDisclaimerData = levelTwo || [];

  const isComplianceItem = (item: FooterLevelTwoDisclaimer): item is ComplianceItem => 'imageUri' in item;
  const complianceLogos = socialsAndDisclaimerData.filter(isComplianceItem).filter(item => item.type === logoType);

  const availableSocialLinks = socialLinks.filter(({ href }) => !!href);

  return (
    <section
      aria-label="Footer Socials And Disclaimer Section"
      className={styles.footerSocialsAndDisclaimerWrapper}
      data-testid="footer-socials-and-disclaimer-section"
    >
      <div className={styles.footerSocialsAndDisclaimerContainer}>
        <div className={styles.footerLogoContainer}>
          <a data-testid="logo-link" href="./" rel="noopener noreferrer" target="_blank" title="Oddschecker">
            {logo}
          </a>
        </div>
        {complianceLogos.length ? (
          <div className={styles.complianceLogosContainer}>
            {complianceLogos.map(({ ctaUri, imageUri }) => (
              <ComplianceLogo ctaUri={ctaUri} imageUri={imageUri} key={imageUri} />
            ))}
          </div>
        ) : null}
        <div className={styles.footerRegulationContainer} data-testid="disclaimer">
          <span className={styles.legalAge}>{legalAge}</span>
          {legalText}
        </div>
        <div className={styles.footerSocialsContainer} data-testid="socials">
          {availableSocialLinks.map(({ SvgIcon, href, name }) => {
            return <SocialLinkAnchor SvgIcon={SvgIcon} href={href} key={name} name={name} />;
          })}
        </div>
      </div>
    </section>
  );
};
